// turns url that starts with //foo.bar to https://foo.bar to cater for following error :
// Error: Failed to parse src "//a.storyblok.com/xxx/" on `next/image`, protocol-relative URL (//) must be changed to an absolute URL (http:// or https://)
import config from '../locale.config';
import { kebabCase } from 'lodash';
import type { StoryLink } from './storyblok/types/global.types';
import type { IncomingMessage } from 'http';

export const protocolRelativeToAbsoluteUrl = (url: string): string => {
  if (!url) return url;
  if ((url && url[0] !== '/') || (url && url[1] !== '/')) return url;
  return `https:${url}`;
};

export const cleanUrl = (splittedUrl: Array<string>, endIndex: number): string => {
  splittedUrl.splice(0, endIndex).join('/');
  return `/${splittedUrl.join('/')}`;
};

const containsLanguageOrCountry = (
  urlPart: string,
  currentLanguage: string,
  country?: string,
): boolean =>
  !!(urlPart && urlPart.toLocaleLowerCase() === currentLanguage.toLocaleLowerCase()) ||
  !!(country && urlPart && urlPart.toLocaleLowerCase() === country.toLocaleLowerCase());

export const stripLocaleFromUrl = (url: string, locale?: string): string => {
  /**
   * When it's un NON-relinked url that is cloned from other country-folder:
   * Clean the country-folder (fr/nl/en) from the URL and use NextLink to set the correct current locale (fr-fr, fr-en, nl-nl, nl-en, etc).
   */
  const currentLanguage = config.getLanguageByLocale(locale);
  const currentCountry = config.getCountryFolderNameByLocale(locale);
  const splittedUrl = url.split('/');
  let newUrl = url;

  /**
   * Example Storyblok output: '/fr/page' - (/{countryFolder}/page)
   */
  if (
    !splittedUrl[0] &&
    containsLanguageOrCountry(splittedUrl[1], currentLanguage, currentCountry)
  ) {
    newUrl = cleanUrl(splittedUrl, 2);
  } else if (containsLanguageOrCountry(splittedUrl[0], currentLanguage, currentCountry)) {
    /**
     * Example Storyblok output: 'fr/page' - ({countryFolder}/page)
     */
    newUrl = cleanUrl(splittedUrl, 1);
  }

  /**
   * Example Storyblok output: /fr-nl/page:
   */
  const localeMatch = config.locales.find((locale) => newUrl.startsWith(`/${locale}/`));
  if (localeMatch) {
    newUrl = newUrl.replace(`/${locale}`, '');
  }

  /**
   * Example Storyblok output: /{clonedFromCountryFolder}/{countryFolder}/page:
   * NON-relinked cases(resolved_relations) that are cloned from other country-folder need to be cleaned.
   */
  const match = config.countryFolders.find((country) => newUrl.startsWith(`/${country}/`));
  if (match) {
    newUrl = newUrl.replace(`/${match}`, '');
  }

  if (newUrl.indexOf('/') === 0) {
    newUrl = newUrl.replace('/', '');
  }

  return newUrl;
};

export function dataSourceToUrlQuery(label: string): string {
  return kebabCase(label);
}

export function stripQueriesFromUrl(path: string): string {
  let newPath = path;
  if (path.includes('?')) {
    newPath = path.slice(0, path.indexOf('?'));
  }
  return newPath;
}

export function getLink(link?: StoryLink, locale?: string): string | undefined {
  let cleanLink;
  if (link?.cached_url) {
    cleanLink = stripLocaleFromUrl(link?.cached_url, locale);
  }

  return cleanLink;
}

export function getHostName(req?: IncomingMessage, localhostAddress = 'localhost:3000'): string {
  let host = process.env.DOMAIN?.replace('https://', '');

  if (!host) {
    host = (req?.headers ? req.headers.host : window.location.host) || localhostAddress;

    if (req && req.headers.Host && typeof req.headers.Host === 'string') {
      host = req.headers.Host;
    }

    if (
      req &&
      req.headers['x-forwarded-host'] &&
      typeof req.headers['x-forwarded-host'] === 'string'
    ) {
      host = req.headers['x-forwarded-host'];
    }

    if (req && req.headers[':authority'] && typeof req.headers[':authority'] === 'string') {
      host = req.headers[':authority'];
    }
  }

  return host;
}

export function fileName(url: string): string {
  const splittedUrl = url.split('/');
  return splittedUrl[splittedUrl.length - 1];
}
